import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Konva from 'konva'
import {
    Stage,
    Layer,
    Image,
    Group,
    Rect,
} from 'react-konva'
import { Position } from 'helpers/math'
import { v4 } from 'uuid'
import { CollaborativeIndexationContainer } from 'components'
import { updateCursor } from 'helpers/media'

export class Img extends Component {

    static propTypes = {
        location: PropTypes.object.isRequired,
        sourceLoading: PropTypes.func.isRequired,
        sourceLoaded: PropTypes.func.isRequired,
        isImageReadyToBeDisplayed: PropTypes.bool.isRequired,
        frame: PropTypes.object.isRequired,
        filters: PropTypes.object.isRequired,
        initImageRect: PropTypes.func.isRequired,
        initProjection: PropTypes.func.isRequired,
        isInvertActive: PropTypes.bool.isRequired,
        computeViewData: PropTypes.func.isRequired,
        id: PropTypes.string.isRequired,
        areFiltersApplied: PropTypes.bool.isRequired,
        unableToLoadMedia: PropTypes.func.isRequired,
        zoomIn: PropTypes.func.isRequired,
        zoomOut: PropTypes.func.isRequired,
        keepImageInBoundaries: PropTypes.func.isRequired,
        updateImageReferencePosition: PropTypes.func.isRequired,
        scaleFactor: PropTypes.number.isRequired,
        isInteractive: PropTypes.bool.isRequired,
        onUnmount: PropTypes.func.isRequired,
    }

    state = {
        cursor: 'cursor-default',
    }

    currentImageUuid = null

    constructor(props) {
        super(props)

        this.image = new window.Image()
        this.image.crossOrigin = "anonymous"

        this.state = { frameCenterPosition: null }

        this.pinchDistance = null

        this.initPreviousPosition = this.initPreviousPosition.bind(this)
        this.loadImage = this.loadImage.bind(this)
        this.onLoad = this.onLoad.bind(this)
        this.dragBoundFunc = this.dragBoundFunc.bind(this)
        this.init = this.init.bind(this)
        this.zoom = this.zoom.bind(this)
        this.zoomPinch = this.zoomPinch.bind(this)
        this.filters = this.filters.bind(this)
        this.isOnLoadEventObsolete = this.isOnLoadEventObsolete.bind(this)
    }

    init(props)
    {
        const { location } = props

        this.image.addEventListener("load", this.onLoad)
        this.image.addEventListener("error", this.props.unableToLoadMedia)

        this.loadImage(location.original)
    }

    initPreviousPosition()
    {
        this.previousPosition = this.state.frameCenterPosition.duplicate()
    }

    loadImage(location)
    {
        const { sourceLoading } = this.props

        sourceLoading()

        this.currentImageUuid = v4()

        this.image.src = location
        this.image.dataset.uuid = this.currentImageUuid
    }

    onLoad(event)
    {
        const {sourceLoaded, initImageRect, initProjection} = this.props

        // If we quickly navigate in media, multiple onLoad will be fired and, as this function will be executed asynchronously and we
        // cannot ensure order of execution, with this fix we ensure that only current image (last media displayed) will have onLoad event handled
        if(this.isOnLoadEventObsolete(event))
        {
            return
        }

        initImageRect(this.image.width, this.image.height)
        initProjection()
        sourceLoaded()

        const { frameWithoutRotationApplied } = this.props.computeViewData()
        this.setState({
            frameCenterPosition : new Position(
                frameWithoutRotationApplied.width / 2,
                frameWithoutRotationApplied.height / 2
        )})

        this.initPreviousPosition()
    }

    isOnLoadEventObsolete(event)
    {
        return this.currentImageUuid !== event.currentTarget.dataset.uuid
    }

    dragBoundFunc(pos)
    {
        if(! this.props.isInteractive)
        {
            return pos
        }

        const { updateImageReferencePosition, scaleFactor } = this.props

        const currentPosition = new Position(pos.x, pos.y)
        updateImageReferencePosition(scaleFactor, this.previousPosition.createVector(currentPosition))
        this.previousPosition = currentPosition.duplicate()

        // hack: only image offset change, not its position (which is fixed, and is always the center of the frame)
        return this.state.frameCenterPosition
    }

    zoom(event)
    {
        const delta = event.evt.deltaY
        const absolutePointerPosition = new Position(event.evt.offsetX, event.evt.offsetY)
        const pointerPositionRelativeToFrameCenter = this.state.frameCenterPosition.createVector(absolutePointerPosition).convertToPosition()

        if(! this.props.isInteractive)
        {
            return
        }

        if(delta < 0) return this.props.zoomIn(pointerPositionRelativeToFrameCenter)

        return this.props.zoomOut(pointerPositionRelativeToFrameCenter)
    }

    zoomPinch(event)
    {
        if(! event.evt.touches || event.evt.touches.length !== 2)
        {
            return
        }

        const touch1 = {
            x: event.evt.touches[0].clientX,
            y: event.evt.touches[0].clientY,
        }
        const touch2 = {
            x: event.evt.touches[1].clientX,
            y: event.evt.touches[1].clientY,
        }

        const absolutePointerPosition = new Position((touch1.x + touch2.x) / 2,(touch1.y + touch2.y) / 2)
        const pointerPositionRelativeToFrameCenter = this.state.frameCenterPosition.createVector(absolutePointerPosition).convertToPosition()
        const distanceBetweenTouches = Math.sqrt(Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2))

        if(this.pinchDistance === null)
        {
            this.pinchDistance = distanceBetweenTouches

            return
        }

        if(this.pinchDistance < distanceBetweenTouches)
        {
            this.props.zoomIn(pointerPositionRelativeToFrameCenter)
        }

        if(this.pinchDistance > distanceBetweenTouches)
        {
            this.props.zoomOut(pointerPositionRelativeToFrameCenter)
        }

        this.pinchDistance = distanceBetweenTouches
    }

    filters()
    {
        const { isInvertActive } = this.props

        const filters = [Konva.Filters.Brighten, Konva.Filters.Contrast]
        if(isInvertActive)
        {
            filters.push(Konva.Filters.Invert)
        }

        return filters
    }

    updateCursor(cursor)
    {
        return (event) => {
            if(! this.props.isInteractive)
            {
                return
            }

            updateCursor(cursor)(event)
        }
    }

    componentDidMount()
    {
        this.init(this.props)
    }

    componentWillReceiveProps(nextProps)
    {
        if(this.props.forcedScale !== nextProps.forcedScale || ! this.props.frame.equals(nextProps.frame))
        {
            this.init(nextProps)
        }

        if(this.props.location.original !== nextProps.location.original)
        {
            this.loadImage(nextProps.location.original)
        }
    }

    componentDidUpdate()
    {
        if(! this.props.isImageReadyToBeDisplayed)
        {
            return
        }

        this.props.keepImageInBoundaries()

        if(this.imageNode)
        {
            const { computeViewData, areFiltersApplied } = this.props
            const { viewRect } = computeViewData()

            this.imageNode.clearCache()

            if(areFiltersApplied)
            {
                this.imageNode.cache({
                    ...viewRect.position,
                    width: viewRect.width,
                    height: viewRect.height,
                })
            }

            this.imageNode.getLayer().batchDraw()
        }
    }

    componentWillUnmount()
    {
        this.props.onUnmount()

        if(! this.image)
        {
            return
        }

        this.image.removeEventListener("load", this.onLoad)
        this.image.removeEventListener("error", this.props.unableToLoadMedia)
    }

    render()
    {
        if(! this.props.isImageReadyToBeDisplayed || ! this.state.frameCenterPosition)
        {
            return null
        }

        const {
            isInteractive,
            id,
            computeViewData,
            filters: { brightness, contrast },
        } = this.props

        const { offset, width, height, rotation, frame, frameWithoutRotationApplied } = computeViewData()

        return (
            <Stage
                width={frameWithoutRotationApplied.width}
                height={frameWithoutRotationApplied.height}
                margin={'auto'}
            >
                <Layer>
                    <Group
                        draggable={isInteractive}
                        dragBoundFunc={pos => this.dragBoundFunc(pos) }
                        onDragEnd={event => {
                            this.pinchDistance = null
                            this.updateCursor('grab')(event)
                            this.initPreviousPosition()
                        }}
                        onDragMove={event => {
                            this.zoomPinch(event)
                            this.updateCursor('grabbing')
                        }}
                        onWheel={event => this.zoom(event)}
                        position={this.state.frameCenterPosition}
                    >
                        <Image
                            ref={node => this.imageNode = node}
                            image={this.image}
                            offset={{...offset}}
                            width={width}
                            height={height}
                            rotation={rotation}
                            filters={this.filters()}
                            brightness={brightness}
                            contrast={contrast}
                            onMouseOver={this.updateCursor('grab')}
                            onMouseOut={this.updateCursor('default')}
                        />
                        <CollaborativeIndexationContainer parentId={id} frame={frame} offset={offset} />
                    </Group>
                </Layer>
            </Stage>
        )
    }
}
